/**
 * Ad refresh (Atlas ch.8).
 *
 * Refresh is commonly 20–40% of a publisher's display revenue, and it is also
 * the fastest way to destroy that revenue: refresh an off-screen slot and you
 * manufacture an unviewed impression, viewability rate collapses, and every
 * buyer lowers its bid for the inventory. So the design is mostly a list of
 * things that BLOCK a refresh, and this class is that list made executable.
 *
 * Viewport state is read locally. It never reads the beacon pipeline: the
 * `viewable` beacon is consent-gated and would silently disable refresh for
 * most European traffic, while `IntersectionObserver` state is not personal
 * data and is available regardless of consent (R8).
 */

/** MRC display: 50% of pixels, or 30% for large formats (>= 242,500 px). */
export const MRC_RATIO = 0.5;
export const MRC_LARGE_RATIO = 0.3;
export const MRC_LARGE_PX = 242_500;

export const REFRESH_DEFAULTS = {
  /** R2: below 30 s buyers object and Google's policies get uncomfortable. */
  intervalMs: 30_000,
  intervalFloorMs: 30_000,
  /** R5. */
  maxPerPageview: 10,
  maxPerPageviewCeiling: 20,
  /** R4: an abandoned tab generating impressions is invalid-traffic territory. */
  idleTimeoutMs: 300_000,
};

/** The viewability ratio this unit must meet. Large formats get the lower bar
 *  because the MRC standard says so, not because they are easier. */
export function ratioFor(areaPx) {
  return areaPx >= MRC_LARGE_PX ? MRC_LARGE_RATIO : MRC_RATIO;
}

/**
 * Clamp publisher config into what policy allows.
 *
 * The dashboard cannot set a faster interval or a higher cap than the ceiling —
 * these are not preferences. A publisher who could refresh every 5 seconds would
 * destroy their own inventory's value and then blame the tag.
 */
export function normaliseRefreshConfig(cfg = {}) {
  const d = REFRESH_DEFAULTS;
  const num = (v, fallback) => (typeof v === "number" && Number.isFinite(v) ? v : fallback);
  return {
    enabled: cfg.enabled === true,
    intervalMs: Math.max(d.intervalFloorMs, num(cfg.intervalMs, d.intervalMs)),
    maxPerPageview: Math.min(d.maxPerPageviewCeiling, Math.max(0, num(cfg.maxPerPageview, d.maxPerPageview))),
    idleTimeoutMs: Math.max(0, num(cfg.idleTimeoutMs, d.idleTimeoutMs)),
  };
}

/**
 * Drives refresh for ONE slot.
 *
 * The dwell clock is the heart of it: it only advances while the ad is both
 * sufficiently in view and on a visible tab. Scroll away and it PAUSES rather
 * than resetting — a visitor who looks, glances away and looks back has still
 * spent that time with the ad, and resetting would punish normal reading
 * behaviour by never refreshing at all (R3).
 */
export class RefreshController {
  /**
   * @param {object} opts
   * @param {object} opts.config              Raw publisher config; normalised here.
   * @param {number} opts.areaPx              Creative area, for the MRC ratio.
   * @param {() => Promise<unknown>} opts.onRefresh  Runs a FULL auction (R6).
   * @param {() => number} [opts.now]
   */
  constructor({ config, areaPx = 0, onRefresh, now = () => Date.now() }) {
    this.cfg = normaliseRefreshConfig(config);
    this.ratio = ratioFor(areaPx);
    this.onRefresh = onRefresh;
    this.now = now;

    this.dwellMs = 0;
    this.count = 0;
    this.inView = false;
    this.tabVisible = true;
    this.lastInteractionAt = now();
    this.inFlight = false;
    this.lastTickAt = now();
  }

  /** @param {number} visibleRatio 0..1 from IntersectionObserver. */
  onVisibility(visibleRatio) {
    this.#accrue();
    this.inView = visibleRatio >= this.ratio;
  }

  onTabVisibility(visible) {
    this.#accrue();
    this.tabVisible = visible === true;
  }

  /** Any pointer, keyboard, scroll or touch event. */
  onInteraction() {
    this.lastInteractionAt = this.now();
  }

  get idle() {
    return this.now() - this.lastInteractionAt >= this.cfg.idleTimeoutMs;
  }

  get eligible() {
    return this.inView && this.tabVisible;
  }

  /** In-view time still owed before the next refresh may fire (0 once met).
   *  Brings the dwell clock up to now first, so a page-side scheduler can arm
   *  one timer for exactly this long instead of polling. */
  remainingDwellMs() {
    this.#accrue();
    return Math.max(0, this.cfg.intervalMs - this.dwellMs);
  }

  /** Why a refresh is blocked, or null when it may proceed. Returning the reason
   *  rather than a boolean so the SDK can report WHY a publisher's refresh never
   *  fires — the commonest support question this feature will generate. */
  blockedReason() {
    if (!this.cfg.enabled) return "disabled";
    if (this.inFlight) return "auction in flight";          // R9
    if (this.count >= this.cfg.maxPerPageview) return "cap reached";  // R5
    if (!this.tabVisible) return "tab hidden";              // R1
    if (!this.inView) return "not in view";                 // R1
    if (this.idle) return "idle";                           // R4
    if (this.dwellMs < this.cfg.intervalMs) return "dwell not met";   // R2/R3
    return null;
  }

  /**
   * Advance the clock and refresh if everything allows it.
   * Call on a timer; it is idempotent and cheap.
   */
  async tick() {
    this.#accrue();
    const blocked = this.blockedReason();
    if (blocked) return { refreshed: false, reason: blocked };

    this.inFlight = true;
    try {
      await this.onRefresh();
      this.count += 1;
      // Reset only on a completed refresh. Resetting on attempt would let a
      // failing auction burn the dwell and quietly stop refreshing.
      this.dwellMs = 0;
      return { refreshed: true, count: this.count };
    } catch (err) {
      return { refreshed: false, reason: "refresh failed", error: err };
    } finally {
      this.inFlight = false;
    }
  }

  /** Accrue viewed time since the last observation, only while eligible. */
  #accrue() {
    const t = this.now();
    if (this.eligible) this.dwellMs += Math.max(0, t - this.lastTickAt);
    this.lastTickAt = t;
  }
}
